import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle, DownloadSimple, LockSimple, FileText, CreditCard } from "@phosphor-icons/react";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const publicApi = axios.create({ baseURL: API });
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function DeliveryDownload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/delivery/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link is invalid."));
  }, [token]);

  const confirm = async () => {
    setConfirming(true);
    try {
      const r = await publicApi.post(`/public/delivery/${token}/confirm`, { name });
      setData((d) => ({ ...d, confirmed_received_at: r.data.confirmed_received_at }));
      toast.success("Thank you — we've noted that you received it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not confirm");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="delivery-page">
      <div className="flex-1 w-full max-w-xl mx-auto px-5 py-10">
        <img src={LOGO_URL} alt="Unity Language Group" className="h-12 mb-8" />
        {error && <div className="card-flat p-6 text-red-700" data-testid="delivery-error">{error}</div>}
        {!data && !error && <div className="text-slate-400">Loading…</div>}
        {data && (
          <div className="card-flat p-6 sm:p-8 space-y-6 bg-white">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Your translation</div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="delivery-title">{data.title}</h1>
              <div className="text-sm text-slate-500 mt-1">{data.source_language || "—"} → {data.target_language || "—"}</div>
            </div>
            {data.locked ? <Locked payUrl={data.pay_url} /> : (
              <>
                <div className="space-y-2">
                  {data.files.map((f) => (
                    <a key={f.delivery_id} href={`${API}/public/delivery/${token}/download/${f.delivery_id}`}
                       className="flex items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-[#1E3A8A] hover:bg-slate-50 transition-colors"
                       data-testid={`delivery-download-${f.delivery_id}`}>
                      <FileText size={22} className="text-[#1E3A8A]" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{f.filename}</div>
                        <div className="text-xs text-slate-500">{(f.size || 0) < 1024 ? `${f.size || 0} bytes` : `${Math.round(f.size / 1024)} KB`}</div>
                      </div>
                      <DownloadSimple size={20} className="text-slate-500" />
                    </a>
                  ))}
                </div>
                {data.confirmed_received_at ? (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="delivery-confirmed">
                    <CheckCircle size={20} weight="fill" /> Receipt confirmed {fmtDT(data.confirmed_received_at)}. Thank you!
                  </div>
                ) : (
                  <div className="p-4 rounded-lg border border-slate-200 space-y-3">
                    <div className="text-sm text-slate-700">Got your file? Let us know it arrived safely.</div>
                    <Input placeholder="Your name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="delivery-confirm-name" />
                    <Button onClick={confirm} disabled={confirming} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="delivery-confirm-btn">
                      <CheckCircle size={16} className="mr-1.5" /> {confirming ? "Saving…" : "I received it"}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}

function Locked({ payUrl }) {
  return (
    <div className="p-5 rounded-lg bg-amber-50 border border-amber-200 space-y-3" data-testid="delivery-locked">
      <div className="flex items-center gap-2 font-semibold text-amber-900"><LockSimple size={18} /> Unlocks after payment</div>
      <p className="text-sm text-amber-900">Your translation is complete. It will be released here and emailed to you as soon as payment is received.</p>
      {payUrl && (
        <a href={payUrl} data-testid="delivery-pay-btn">
          <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"><CreditCard size={16} className="mr-1.5" /> Pay now</Button>
        </a>
      )}
    </div>
  );
}
